import {
  Dispatch, useState,
  PropsWithChildren, SetStateAction, createContext, useContext, useMemo,
} from 'react';
import { defaultGuid } from '@equifinance/shared';
import { HistoricData } from '../../api/useProductsApi';
import { ChecklistSection } from '../../components/Checklist/ChecklistPage';
import {
  Checklist,
  ChecklistNotePayload,
  useChecklist,
  ChecklistItem,
  SelectedProduct,
  ChecklistOverrideProductPayload,
  UploadChecklistDocumentPayload,
  AvmSearchTypes,
} from '.';
import { Stages } from '../../types/Stages';

export type ChecklistContextType = {
  id: string,
  historicData: HistoricData,
  checklist: Checklist,
  setChecklistData: Dispatch<SetStateAction<Checklist>>,
  updateChecklist(field: string, value: any, disableIncrementUpdateCount?: boolean): Promise<void>,
  fetchChecklist(): Promise<void>,
  sendOffer(verificationTypes?: string[]): Promise<void>,
  cancelOffer(): Promise<void>,
  resendOffer(): Promise<void>,
  runAvmSearch(avmType: AvmSearchTypes): Promise<void>,
  uploadChecklistDocuments(documentPayload: UploadChecklistDocumentPayload): Promise<Checklist>,
  postNewNote({ checklistSectionId, note }: ChecklistNotePayload): Promise<void>,
  overrideProduct(payload: ChecklistOverrideProductPayload): Promise<void>,
  expandedStates: Record<string, boolean>,
  handleToggleAll: (expanded: boolean) => void,
  handleToggle: (title: ChecklistSection | string) => void,
  setExpandedState: (title: string, expanded: boolean) => void
}

export const defaultChecklistItem: ChecklistItem = {
  version: 1,
  id: defaultGuid,
  status: 'AwaitingDecision',
  stage: Stages.AtLender,
  isLocked: false,
  checklistIndicatorType: 'AttentionNeeded',
  canSubmit: false,
  submitted: false,
  submittedById: defaultGuid,
  submittedByName: '',
  submittedTimestamp: '',
  notes: [],
};

export const defaultSelectedProduct: SelectedProduct = {
  id: defaultGuid,
  productId: defaultGuid,
  type: 'Fixed',
  productCode: '',
  initialRate: '',
  initialPayment: 0,
};

export const defaultChecklist: Checklist = {
  updateCount: 0,
  dirtyCount: 0,

  id: defaultGuid,
  errors: {},
  warnings: {},
  stage: Stages.CreditDecision,
  postOffer: null,
  securityCall: null,
  sendOffer: null,
  fraudChecks: null,
  bankruptcyChecks: null,
  income: null,
  electoralRoll: null,
  product: {
    underwriterSelectedProduct: {
      ...defaultSelectedProduct,
    },
    brokerSelectedProduct: {
      ...defaultSelectedProduct,
    },
    overrideNotes: [],
    justification: '',
    authorisedById: defaultGuid,
    authorisedByName: '',
    decision: null,
    possibleDecisions: [],
    ...defaultChecklistItem,
  },
  policyShields: null,
  landRegistry: null,
  reportOnTitle: null,
  additionalSupport: null,
  valuation: null,
  os1os3: null,
};

const defaultChecklistContext: ChecklistContextType = {
  id: defaultGuid,
  historicData: { productReferences: [] },
  checklist: defaultChecklist,
  setChecklistData: () => { },
  updateChecklist: async () => { },
  fetchChecklist: async () => { },
  sendOffer: async () => { },
  cancelOffer: async () => { },
  resendOffer: async () => { },
  runAvmSearch: async () => { },
  uploadChecklistDocuments: async () => defaultChecklist,
  postNewNote: async () => { },
  overrideProduct: async () => { },
  expandedStates: {},
  handleToggleAll: () => { },
  handleToggle: () => { },
  setExpandedState: () => { },
};

export const ChecklistContext = createContext<ChecklistContextType>(defaultChecklistContext);

export function useChecklistContext() {
  return useContext(ChecklistContext);
}

const checklistNotes: string[] = [
  ChecklistSection.NOTES_POST,
  ChecklistSection.NOTES_SEND_OFFER,
  ChecklistSection.NOTES_AML,
  ChecklistSection.NOTES_BANKRUPTCY,
  ChecklistSection.NOTES_PRODUCT,
  ChecklistSection.OVERRIDE_NOTES_PRODUCT,
  ChecklistSection.NOTES_VALUATION,
  ChecklistSection.NOTES_OS1_OS3,
  ChecklistSection.NOTES_POLICY_SHIELDS,
];

export function ChecklistProvider({ id, children }: PropsWithChildren<{ id: string }>) {
  const checklist = useChecklist(id);

  // All sections expand by default,
  // except notes sections
  const [expandedStates, setExpandedStates] = useState<Record<string, boolean>>(
    Object.values(ChecklistSection).reduce((acc, title) => ({
      ...acc,
      [title]: !checklistNotes.includes(title),
    }), {}),
  );

  // Expand all/Collapsed work on all sections,
  // but Expand all should not work on notes sections
  const handleToggleAll = (expanded: boolean) => {
    const OS1OS3TitleSectionIds = checklist?.checklist?.os1os3?.titles.map((t) => t.id) ?? [];
    const newStates = [...Object.values(ChecklistSection), ...OS1OS3TitleSectionIds].reduce((acc, title) => ({
      ...acc,
      [title]: expanded
        ? !checklistNotes.includes(title)
          ? expanded
          : expandedStates[title]
        : false,
    }), {});
    setExpandedStates(newStates);
  };

  const handleToggle = (title: ChecklistSection | string) => {
    setExpandedStates((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  const setExpandedState = (title: string, expanded: boolean = false) => {
    setExpandedStates((prev) => ({
      ...prev,
      [title]: expanded,
    }));
  };

  const contextValue = useMemo(() => ({
    ...checklist, expandedStates, handleToggleAll, handleToggle, id, setExpandedState,
  }), [id, checklist, expandedStates]);

  return (
    <ChecklistContext.Provider value={contextValue}>{children}</ChecklistContext.Provider>
  );
}
